import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Package, ChevronRight, ClipboardList } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import TopHeader from '../components/layout/TopHeader.jsx'
import { StatusBadge, EmptyState } from '../components/ui/index.jsx'
import { orderService, publicSettingsService } from '../services/api.js'
import { useAuthStore } from '../store/index.js'
import toast from 'react-hot-toast'

const STATUS_FILTERS = ['All', 'Active', 'Delivered', 'Cancelled']

// Status change emoji map
const STATUS_EMOJIS = {
  processing: '⚙️', confirmed: '✅', shipped: '🚚',
  delivered: '🎉', completed: '🎉', cancelled: '❌',
}

const STATUS_MESSAGES = {
  processing: 'is now being processed',
  confirmed: 'has been confirmed',
  shipped: 'has been shipped — on its way!',
  delivered: 'has been delivered',
  completed: 'is complete',
  cancelled: 'has been cancelled',
}

// Hook to detect status changes and fire toasts
function useOrderStatusToasts(orders) {
  const prevStatusRef = useRef({})

  useEffect(() => {
    if (!orders?.length) return
    const prev = prevStatusRef.current
    const hasSeenOrders = Object.keys(prev).length > 0

    orders.forEach(order => {
      const prevStatus = prev[order.id]
      const newStatus = order.status

      if (hasSeenOrders && prevStatus && prevStatus !== newStatus) {
        const emoji = STATUS_EMOJIS[newStatus] || '📦'
        const msg = STATUS_MESSAGES[newStatus] || `is now ${newStatus}`
        const isGood = ['confirmed', 'shipped', 'delivered', 'completed'].includes(newStatus)

        if (isGood) {
          toast.success(`${emoji} Order #${order.id} ${msg}`, {
            duration: 5000,
            style: { fontFamily: 'inherit' },
          })
        } else if (newStatus === 'cancelled') {
          toast.error(`${emoji} Order #${order.id} ${msg}`, { duration: 5000 })
        } else {
          toast(`${emoji} Order #${order.id} ${msg}`, { duration: 4000 })
        }
      }
    })

    // Update previous statuses
    const next = {}
    orders.forEach(o => { next[o.id] = o.status })
    prevStatusRef.current = next
  }, [orders])
}


function statusGroup(s) {
  if (['pending','processing','shipped'].includes(s)) return 'Active'
  if (s === 'delivered') return 'Delivered'
  if (s === 'cancelled') return 'Cancelled'
  return 'Active'
}

export default function Orders() {
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore(s => s.isAuthenticated)
  const [filter, setFilter] = useState('All')

  const { data, isLoading } = useQuery({
    queryKey: ['orders', 'mine'],
    queryFn: orderService.getMine,
    enabled: isAuthenticated,
    staleTime: 2 * 60 * 1000,
    refetchInterval: isAuthenticated ? 60 * 1000 : false, // poll every 60s
  })

  const { data: pubSettings } = useQuery({
    queryKey: ['public-settings'],
    queryFn: () => publicSettingsService.get().then(r => r.data),
    staleTime: 5 * 60 * 1000,
  })
  const showOrderStatus = pubSettings?.show_order_status ?? false

  const orders = Array.isArray(data) ? data : (data?.items ?? data?.orders ?? [])

  // Fire toast notifications when order status changes
  useOrderStatusToasts(orders)

  if (!isAuthenticated) return (
    <div className="page-container"><TopHeader title="My Orders"/>
      <EmptyState icon={ClipboardList} title="Sign in to view orders" message="Track your orders and view purchase history" action={()=>navigate('/login')} actionLabel="Sign In"/>
    </div>
  )

  const filtered = orders.filter(o => filter === 'All' || statusGroup(o.status) === filter)

  return (
    <div className="page-container bg-surface-dark">
      <TopHeader title="My Orders"/>

      {/* Filter tabs — only shown when status is visible */}
      {showOrderStatus && (
        <div className="flex gap-2 px-4 py-3 overflow-x-auto scroll-hide">
          {STATUS_FILTERS.map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-display font-semibold transition-colors ${filter===f?'bg-primary text-white':'bg-surface-card text-gray-400 border border-surface-border'}`}>
              {f}
            </button>
          ))}
        </div>
      )}

      <div className="px-4 space-y-3 pb-4">
        {isLoading ? (
          Array(3).fill(0).map((_,i)=>(
            <div key={i} className="bg-surface-card rounded-2xl p-4 animate-pulse">
              <div className="h-4 bg-surface-elevated rounded w-1/3 mb-2"/>
              <div className="h-3 bg-surface-elevated rounded w-1/2"/>
            </div>
          ))
        ) : filtered.length === 0 ? (
          <EmptyState icon={Package} title="No orders yet" message="Start shopping to see your orders here" action={()=>navigate('/shop')} actionLabel="Shop Now"/>
        ) : filtered.map(order => (
          <div key={order.id} onClick={() => navigate(`/orders/${order.id}`)} className="bg-surface-card rounded-2xl p-4 border border-surface-border active:border-primary/30 transition-colors cursor-pointer">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="font-display font-semibold text-white text-sm">Order #{order.id}</p>
                <p className="text-xs text-gray-500 mt-0.5">{(order.order_date || order.created_at) ? new Date(order.order_date || order.created_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</p>
              </div>
              {showOrderStatus && <StatusBadge status={order.status}/>}
            </div>
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-400 font-display">
                {order.items?.length ?? '—'} item{(order.items?.length ?? 0) !== 1 ? 's' : ''}
              </div>
              <div className="flex items-center gap-2">
                <span className="font-heading text-white text-sm">R{parseFloat(order.total_amount||0).toFixed(2)}</span>
                <ChevronRight size={14} className="text-gray-500"/>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
